import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import * as commService from '../../services/caseRequestCommunications';
import type { CaseRequestCommunicationMessage } from '../../types/caseRequestCommunications';
import { formatCommunicationTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';

const MAX_COMMENT_LENGTH = 2000;

type Props = {
  caseRequestId: string;
};

function senderDisplayName(msg: CaseRequestCommunicationMessage): string {
  return msg.senderSubtitle.trim() || msg.senderName.trim() || '—';
}

/** Internal (AGD): name with title + role. External: name with title + agency. */
function senderLabel(msg: CaseRequestCommunicationMessage): string {
  const name = senderDisplayName(msg);
  if (msg.intranet) {
    const role = msg.role.trim();
    return role ? `${name} - ${role}` : name;
  }
  const agency = msg.agency.trim();
  return agency ? `${name} - ${agency}` : name;
}

export default function ViewRequestCommunicationsTab({ caseRequestId }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [messages, setMessages] = useState<CaseRequestCommunicationMessage[]>([]);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const submittingRef = useRef(false);

  const loadThread = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    const r = await commService.getCaseRequestCommunications(caseRequestId);
    setLoading(false);
    if (!r.success) {
      const msg = r.message ?? t('viewRequest.communicationsLoadFailed');
      setLoadError(msg);
      setFlashToast({ message: msg, variant: 'error' });
      return;
    }
    setMessages(r.data.messages);
  }, [caseRequestId, t]);

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  const submitComment = async () => {
    const text = comment.trim();
    if (!text) {
      setFlashToast({ message: 'Please enter a comment.', variant: 'error' });
      return;
    }
    if (text.length > MAX_COMMENT_LENGTH) {
      setFlashToast({ message: `Comment must be ${MAX_COMMENT_LENGTH} characters or fewer.`, variant: 'error' });
      return;
    }
    if (submittingRef.current) return;

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const r = await commService.addCaseRequestComment(caseRequestId, text, {
        visibleToExternalAgency: true,
      });

      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Could not add comment.', variant: 'error' });
        return;
      }

      setComment('');
      setFlashToast({ message: 'Comment added successfully.', variant: 'success' });
      void loadThread();
    } finally {
      // Keep the lock for one extra tick to avoid a fast re-click
      // before React has a chance to re-render/clear/disable controls.
      setTimeout(() => {
        submittingRef.current = false;
        setSubmitting(false);
      }, 0);
    }
  };

  if (loading) {
    return <p className="vcr-loading">{t('viewRequest.loadingCommunications')}</p>;
  }

  if (loadError) {
    return <p className="vcr-error">{loadError}</p>;
  }

  const handleCancelClick = () => {
    setCancelConfirmOpen(true);
  };

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          setComment('');
          setCancelConfirmOpen(false);
        }}
      />

    <div className="vcr-comm">
      <h3 className="vcr-comm-title">Communications</h3>

      <div className="vcr-comm-thread">
        {messages.length ? (
          messages.map((msg) => (
            <div key={msg.id} className="vcr-comm-msg">
              <div className="vcr-comm-msg-hdr">
                <span className="vcr-comm-sender">{senderLabel(msg)}</span>
                <span className="vcr-comm-time">{formatCommunicationTime(msg.createdAt)}</span>
              </div>
              <p className="vcr-comm-body">{msg.comment}</p>
            </div>
          ))
        ) : (
          <p className="vcr-comm-empty">{t('common.noDataFound')}</p>
        )}
      </div>

      <h4 className="vcr-comm-add-title">Add a Comment</h4>
      <textarea
        className="vcr-comm-ta"
        placeholder="Add a Comment..."
        value={comment}
        disabled={submitting}
        maxLength={MAX_COMMENT_LENGTH}
        onChange={(e) => setComment(e.target.value)}
        rows={5}
      />
      <p className="vcr-comm-hint">{comment.length}/{MAX_COMMENT_LENGTH} characters</p>
      <div className="vcr-comm-actions">
        <button
          type="button"
          className="vcr-btn-cancel"
          disabled={submitting}
          onClick={handleCancelClick}
        >
          Cancel
        </button>
        <button
          type="button"
          className="vcr-btn-submit"
          disabled={submitting || !comment.trim()}
          onClick={() => void submitComment()}
        >
          {submitting ? 'Adding…' : 'Add Comment'}
        </button>
      </div>
    </div>
    </>
  );
}
